import { useMutation } from "@tanstack/react-query";
import { LinkIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import {
  fetchRequestLogInputVariables,
  RequestToolData,
} from "@/queries/requests/useRequestLogInputVariables";
import { stringifyVariables } from "@/utils/playground";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface RequestImportProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onVariablesFetched?: (variables: Record<string, string>) => void;
  onRequestImported?: (
    requestId: number,
    variables: Record<string, string>,
    toolData?: RequestToolData | null,
  ) => void;
  normalizeImportedVariables?: (variables: Record<string, string>) => Record<string, string>;
}

export const RequestImport = ({
  isOpen,
  onOpenChange,
  onVariablesFetched,
  onRequestImported,
  normalizeImportedVariables,
}: RequestImportProps) => {
  const auth = useAuth();
  const [requestUrl, setRequestUrl] = useState("");

  const importRequestMutation = useMutation(
    (requestLogId: number) => fetchRequestLogInputVariables(auth?.userToken ?? "", requestLogId),
    {
      onSuccess: (response, requestLogId) => {
        const data = response.prompt_input_variables;
        const toolData = response.request_tool_data;
        const hasVariables = Object.keys(data).length > 0;
        const stringVariables = stringifyVariables(data);
        const importedVariables = normalizeImportedVariables?.(stringVariables) ?? stringVariables;

        if (hasVariables) {
          onVariablesFetched?.(importedVariables);
        }

        if (onRequestImported) {
          // Delegate toast handling to the parent (e.g. EditTestCaseDialog)
          // which can show a consolidated message based on both variable
          // import and chat history results.
          onRequestImported(requestLogId, importedVariables, toolData);
        } else if (hasVariables) {
          displayToast("Variables imported successfully!", ToastType.success);
        } else {
          displayErrorToast("No input variables found");
        }

        setRequestUrl("");
        onOpenChange(false);
      },
      onError: () => {
        displayErrorToast("Failed to fetch variables. Please check the URL and try again.");
      },
    },
  );

  const handleImport = () => {
    if (!requestUrl.trim()) {
      displayErrorToast("Please enter a URL");
      return;
    }

    if (!requestUrl.includes("/")) {
      displayErrorToast("Invalid URL format");
      return;
    }

    // Strip query parameters before extracting ID
    const urlWithoutParams = requestUrl.split("?")[0];
    const id = Number(urlWithoutParams.split("/").pop() || "0");

    if (!id || isNaN(id)) {
      displayErrorToast("Invalid ID in URL");
      return;
    }

    importRequestMutation.mutate(id);
  };

  const isImporting = importRequestMutation.isLoading;

  return (
    <Dialog open={isOpen} onOpenChange={open => !isImporting && onOpenChange(open)}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Import from Request</DialogTitle>
          <DialogDescription>
            Paste the URL of a request log to import its input variables.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="request-url">Request URL</Label>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="request-url"
                type="text"
                placeholder="https://promptlayer.com/request/12345"
                value={requestUrl}
                onChange={e => setRequestUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    handleImport();
                  }
                }}
                className="pl-9"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              The request ID will be extracted from the URL automatically.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isImporting}>
              Cancel
            </Button>
            <Button onClick={handleImport} disabled={isImporting}>
              {isImporting ? "Importing…" : "Import Variables"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
